import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Stage, Layer, KonvaNodeEvents, Group} from 'react-konva'
import {Element, TypedElement} from '../../entities/project'
import {ElementTypeId} from '../../entities/elementType'
import {Button, Card, Checkbox, Divider, Input, message, theme} from 'antd'
import {CanvasEditorShape} from './elements/CanvasEditorShape'
import {useZoomSliderSteps, ZoomSlider, ZoomSliderMemoized} from './ZoomSlider'
import {ZoomScaleLine} from './ZoomScaleLine'
import {ModeType} from './editors/EditorCreateElements'
import {Plant} from '../../entities/plant'
import {CanvasEditorPlant} from './elements/CanvasEditorPlant'
import {Compass} from './Compass'
import {useIntl} from 'react-intl'
import {GridLayer} from './visualLayers/GridLayer'
import {MapLayer} from './visualLayers/MapLayer'
import {Stage as KonvaStage} from 'konva/lib/Stage'
import {Layer as KonvaLayer} from 'konva/lib/Layer'
import {canvasElementsRegistry} from './elements/CanvasElementsRegistry'
import {KonvaEventObject, NodeConfig} from 'konva/lib/Node'
import {RulerLayer} from './visualLayers/RulerLayer'
import {DraggableElementLayer} from './visualLayers/DraggableElementLayer'
import {getRelativePointerPosition} from './util'
import {instance as elementTypeService} from '../../services/ElementTypeService'
import {instance as plantsService} from '../../services/PlantsService'
import {CopyOutlined, DeleteOutlined} from '@ant-design/icons'
import {PlantControlCanvasButtons} from './PlantControlCanvasButtons'
import {useResponsiveStageSize} from '../../hooks/canvas/useResponsiveStageSize'
import {useLayoutEffectMemo} from '../../hooks/useLayoutEffectMemo'
import {Language} from '../../services/language'

export const defaultZoomPixelsPerCm = 0.2

export type CanvasEditorProps = {
  projectId: string
  plotShape: Element
  elements: TypedElement[]
  plants?: Plant[]
  selectable: boolean
  plotShapeEditable?: boolean
  onPlotShapeChanged?: (newPlotShape: Element) => void
  toolLayer?: React.ReactNode
  toolCursor?: string
  currentMode?: ModeType
  editableTypes?: ElementTypeId[]
  lockedPlantIds?: number[]
  selectedPlant?: {
    id: number
    name: string
    specimenIndex?: number
  } | null
  setSelectedPlant?: (plant: {id: number; specimenIndex?: number}) => void
  onPlantChanged?: (updatedPlant: TypedElement) => void
  selectedElementId?: number | null
  setSelectedElementId?: (index: number) => void
  onElementChanged?: (updatedElement: TypedElement) => void
  hasMap: boolean
  transformerLayerProps?: any
  activeDraggableElement?: {temaplateId: string; elementTypeId: ElementTypeId}
  onDraggableElementChange?: (name: string) => void
  onDragEnd?: (x: number, y: number, width: number, height: number, templateName: string) => void
  onMouseUp?: () => void
  openPlantById?: (id: number) => void
  digUpPlant?: (diggingPlantId?: number, diggingPlantSpecimenNumber?: number) => void
  duplicatePlant?: (
    duplicatingPlantId: number,
    specimenNumber: number,
    specimenQuantity: number,
    direction?: string
  ) => void
}

function calculateCanvasCenter(elements: TypedElement[], plotShape, canvasPixelsPerCm) {
  if (!plotShape && elements.length === 0) {
    return [0, 0]
  } else {
    const aabbs = elements.map((el) =>
      canvasElementsRegistry[el.shapeType].getAabb(el, canvasPixelsPerCm)
    )

    let minX = Infinity
    let maxX = -Infinity

    let minY = Infinity
    let maxY = -Infinity

    if (plotShape) {
      let plotAabb = canvasElementsRegistry[plotShape.shapeType].getAabb(
        plotShape,
        canvasPixelsPerCm
      )
      minX = plotAabb.xFrom
      maxX = plotAabb.xTo
      minY = plotAabb.yFrom
      maxY = plotAabb.yTo
    }

    for (const {xFrom, xTo, yFrom, yTo} of aabbs) {
      if (xFrom < minX) {
        minX = xFrom
      }
      if (xTo > maxX) {
        maxX = xTo
      }

      if (yFrom < minY) {
        minY = yFrom
      }
      if (yTo > maxY) {
        maxY = yTo
      }
    }

    const centerX = minX + (maxX - minX) / 2
    const centerY = minY + (maxY - minY) / 2

    return [centerX, centerY]
  }
}

export function CanvasEditor({
  projectId,
  plotShape,
  elements,
  plants,
  selectable = false,
  plotShapeEditable,
  onPlotShapeChanged,
  currentMode = null,
  editableTypes = [],
  lockedPlantIds = [],
  toolLayer = null,
  toolCursor = null,
  selectedPlant = null,
  setSelectedPlant = () => {},
  selectedElementId = null,
  setSelectedElementId = () => {},
  onElementChanged = () => {},
  hasMap,
  transformerLayerProps,
  activeDraggableElement,
  onDraggableElementChange = () => {},
  onDragEnd = () => {},
  onMouseUp = () => {},
  digUpPlant = (diggingPlantId?: number, diggingPlantSpecimenNumber?: number) => {},
  openPlantById = (id: number) => {},
  duplicatePlant = (
    duplicatingPlantId: number,
    specimenNumber: number,
    specimenQuantity: number,
    direction?: string
  ) => {},
}: CanvasEditorProps) {
  //const [selectedElementIndex, setSelectedElementIndex] = React.useState(null as number | null)
  const [twiceSelectedElementIndex, setTwiceSelectedElementIndex] = React.useState(
    null as number | null
  )
  const [cursor, setCursor] = React.useState('grab')
  const resetCursor = () => setCursor('grab')

  // const [mousePosition, setMousePosition] = useState({
  //   x: undefined as undefined | number,
  //   y: undefined as undefined | number,
  // })

  const [isVisibleMap, setVisibleMap] = React.useState(false)
  const [isGridVisible, setGridVisible] = React.useState<boolean>(false)
  const [gridSpacing, setGridSpacing] = React.useState<number>(5)
  const [isRulerActive, setRulerActive] = React.useState<boolean>(false)
  const [isMouseDown, setMouseDown] = React.useState(false)

  const stageRef = useRef<KonvaStage | null>(null)
  const elementsLayerRef = useRef<KonvaLayer | null>(null)

  const intl = useIntl()

  const actualCursor = Boolean(toolLayer) ? toolCursor || 'crosshair' : cursor

  const [zoomPixelsPerCm, setZoomPixelsPerCm] = React.useState<number>(defaultZoomPixelsPerCm)

  const zoomSliderSteps = 10
  const zoomSliderMin = 0.1
  const zoomSliderMax = 10

  const [zoomStep, setZoomStep, zoomCoefficient] = useZoomSliderSteps({
    steps: 10,
    min: zoomSliderMin,
    max: zoomSliderMax,
  })

  const {
    token: {controlHeightSM, colorTextTertiary},
  } = theme.useToken()

  const canvasPixelsPerCm = zoomCoefficient * zoomPixelsPerCm

  const checkDeselect = (e) => {
    // deselect when clicked on empty area
    const clickedOnEmpty = e.target === e.target.getStage()
    if (clickedOnEmpty) {
      setSelectedElementId(null)
      setTwiceSelectedElementIndex(null)
    }
  }

  const stageContainerRef = useRef<HTMLDivElement | null>(null)

  // const stageWidth = window.innerWidth - 900 > 550 ? window.innerWidth - 900 : 550
  // const stageHeight = window.innerHeight / 2 > 400 ? window.innerHeight / 2 : 400

  // useEffect(() => {
  //   if (stageRef.current) {
  //     // window['stageRef'] = stageRef.current

  //     console.log(`stage reset position: ${stageRef?.current.x()}, ${stageRef?.current.y()}`)

  //     const centerCoordintes = calculateCanvasCenter(elements, plotShape, canvasPixelsPerCm)

  //     const stageX = centerCoordintes[0] * canvasPixelsPerCm //- stageWidth / 2
  //     const stageY = centerCoordintes[1] * canvasPixelsPerCm //- stageHeight / 2

  //     console.log(`stageX: ${stageX}, stageY: ${stageY}, center: {${centerCoordintes.join(',')}}`)
  //     console.log(`plotShapeProps: ${JSON.stringify(plotShape?.props)}`)

  //     //stageRef.current.x(stageX)
  //     //stageRef.current.y(stageY)

  //     console.log(`stage after reset position: ${stageRef?.current.x()}, ${stageRef?.current.y()}`)
  //   }
  // }, [projectId, plotShape])

  const language = intl.locale as Language

  function createNewPlantSpecimen(plant: Plant, specimenIndex: number) {
    const oldCoordinates = plant.coordinates[specimenIndex]
    const newCoordinates = {
      x: oldCoordinates.x + oldCoordinates.width,
      y: oldCoordinates.y,
      width: oldCoordinates.width,
      height: oldCoordinates.height,
    }
    const newPlant = {...plant, coordinates: [...plant.coordinates, newCoordinates]}
    plantsService.editPlant(newPlant, language)
  }

  function deletePlantSpecimen(plant: Plant, specimenIndex: number) {
    const oldCoordinates = plant.coordinates[specimenIndex]

    const newCoordinates = oldCoordinates
      .slice(0, specimenIndex)
      .concat(oldCoordinates.slice(specimenIndex))

    const newPlant = {...plant, coordinates: newCoordinates}
    plantsService.editPlant(newPlant, language)
  }

  const centerCanvas = useCallback(() => {
    if (!elementsLayerRef.current || !stageRef.current) {
      return
    }

    let stageAabb = elementsLayerRef.current.getClientRect({skipTransform: true})

    let zoomStageX =
      stageAabb.width === 0
        ? defaultZoomPixelsPerCm
        : stageRef.current.attrs.width / stageAabb.width
    let zoomStageY =
      stageAabb.height === 0
        ? defaultZoomPixelsPerCm
        : stageRef.current.attrs.height / stageAabb.height

    const newZoom = Math.min(zoomStageX, zoomStageY)
    const newZoomPixelsPerCm = newZoom / zoomCoefficient
    setZoomPixelsPerCm(newZoomPixelsPerCm)

    if (zoomStageX > zoomStageY) {
      const x = stageAabb.x + stageAabb.width / 2 - stageRef.current.attrs.width / zoomStageY / 2
      const y = stageAabb.y
      stageRef.current.x(-x * newZoom)
      stageRef.current.y(-y * newZoom)
    } else {
      const y = stageAabb.y + stageAabb.height / 2 - stageRef.current.attrs.height / zoomStageX / 2
      const x = stageAabb.x
      stageRef.current.x(-x * newZoom)
      stageRef.current.y(-y * newZoom)
    }

    const event: KonvaEventObject<Event> = {
      type: 'dragmove',
      target: stageRef.current,
      evt: undefined,
      currentTarget: stageRef.current,
      cancelBubble: false,
    }
    stageRef.current.dispatchEvent(event)
  }, [zoomCoefficient])

  const {width: stageWidth, height: stageHeight} = useResponsiveStageSize(
    stageRef,
    stageContainerRef
  )

  useEffect(() => {
    if (stageRef.current) {
      centerCanvas()
    }
  }, [projectId, stageWidth, stageHeight])

  useEffect(() => {
    const onDragTemplateEnd: KonvaNodeEvents['onMouseMove'] = (e) => {
      if (!activeDraggableElement) {
        return
      }
      const {x, y} = getRelativePointerPosition(e.target)
      const template = elementTypeService.getElementTypeTemplates(
        activeDraggableElement.elementTypeId
      )?.[activeDraggableElement.temaplateId]
      if (!template) {
        return
      }
      onDragEnd(x, y, template.width, template.height, activeDraggableElement.temaplateId)
      onDraggableElementChange(null)
    }

    const stage = stageRef.current
    stage.on('mousemove', onDragTemplateEnd)

    return () => {
      if (stage) {
        stage.off('mousemove', onDragTemplateEnd)
      }
    }
  }, [activeDraggableElement])

  const selectedPlantCoordinates = useLayoutEffectMemo(() => {
    if (isMouseDown) {
      return null
    }

    if (!elementsLayerRef.current || !selectedPlant) {
      return null
    }

    const elements = elementsLayerRef.current.children[0]['children'] as
      | KonvaLayer['children']
      | undefined

    if (!elements) {
      return null
    }

    const selectedPlantElement = elements.find(
      (e) =>
        e['_plantId'] === selectedPlant.id && e['_specimenIndex'] === selectedPlant.specimenIndex
    )

    if (!selectedPlantElement) {
      return null
    }

    const scale = selectedPlantElement.getAbsoluteScale()
    const size = selectedPlantElement.getSize()
    const absolutePosition = selectedPlantElement.getAbsolutePosition()

    return {
      x: absolutePosition.x,
      y: absolutePosition.y,
      width: size.width * scale.x,
      height: size.height * scale.y,
    }

    // const plantElements = elementsLayerRef
  }, [selectedPlant, plants, zoomCoefficient, isMouseDown])

  // const [image] = useImage(dots2)
  return (
    <Card
      style={{
        position: 'relative',
        minHeight: '100%',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
      }}
      styles={{body: {minHeight: '100%', width: '100%'}}}
    >
      <div className="setting-box" style={{}}>
        <div className="movement-buttons">
          {/* ({mousePosition?.x}, {mousePosition?.y}) */}

          <Button onClick={centerCanvas}>{intl.formatMessage({id: 'center'})}</Button>
          <Button
            type={isRulerActive ? 'primary' : 'default'}
            onClick={(e) => setRulerActive(!isRulerActive)}
          >
            {intl.formatMessage({id: 'ruler'})}
          </Button>
        </div>
        {hasMap && (
          <Checkbox checked={isVisibleMap} onChange={(e) => setVisibleMap(e.target.checked)}>
            {intl.formatMessage({id: 'showMap'})}
          </Checkbox>
        )}
        <Checkbox
          className="grid-spacing-checkbox"
          checked={isGridVisible}
          onChange={(e) => setGridVisible(e.target.checked)}
          style={{display: 'flex', alignItems: 'baseline'}}
        >
          {intl.formatMessage({id: 'showGridWithSpacing'})}
          <span>
            <Input
              type="number"
              style={{width: '75px'}}
              disabled={!isGridVisible}
              value={gridSpacing}
              onChange={(e) => setGridSpacing(Number.parseInt(e.target.value, 10))}
            />
            <span className="grid-spacing-checkbox-units">{intl.formatMessage({id: 'm'})}</span>
          </span>
        </Checkbox>
      </div>
      <div style={{position: 'relative', height: '100%', overflow: 'hidden'}}>
        <div style={{position: 'absolute', inset: 0}} ref={stageContainerRef}>
          {selectedPlant && selectedPlantCoordinates && (
            <div
              style={{
                position: 'absolute',
                left: selectedPlantCoordinates.x + selectedPlantCoordinates.width,
                top: selectedPlantCoordinates.y,
                // height: selectedPlantCoordinates.height,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                gap: controlHeightSM,
                cursor: 'pointer',
                pointerEvents: 'all',
                zIndex: 1000,
              }}
            >
              <PlantControlCanvasButtons
                plantId={selectedPlant.id}
                specimenNumber={selectedPlant.specimenIndex}
                openPlantById={openPlantById}
                digUpPlant={digUpPlant}
                duplicatePlant={duplicatePlant}
                isLocked={lockedPlantIds.includes(selectedPlant.id)}
              />
            </div>
          )}
          <Stage
            width={stageWidth}
            height={stageHeight}
            onMouseDown={(e) => {
              setMouseDown(true)
              checkDeselect(e)
            }}
            onMouseUp={(e) => {
              setMouseDown(false)
              onMouseUp()
            }}
            onTouchStart={checkDeselect}
            // onMouseMove={(e) => {
            //   const pointerPosition = stageRef.current?.getRelativePointerPosition()
            //   setMousePosition(pointerPosition || {x: undefined, y: undefined})
            // }}
            draggable={!Boolean(toolLayer) && !isRulerActive}
            style={{cursor: actualCursor}}
            scaleX={canvasPixelsPerCm}
            scaleY={canvasPixelsPerCm}
            ref={stageRef}
            onDragMove={() => {
              // console.log(`stage move: ${stageRef?.current.x()}, ${stageRef?.current.y()}`)
            }}
            onWheel={(e) => {
              e.evt.preventDefault()

              const stage = e.target.getStage()
              const oldScale = stage.scaleX()

              const pointerPosition = stage.getPointerPosition()

              const mousePointTo = {
                x: pointerPosition.x / oldScale - stage.x() / oldScale,
                y: pointerPosition.y / oldScale - stage.y() / oldScale,
              }

              const isZoomIn = e.evt.deltaY < 0
              if (isZoomIn) {
                setZoomStep(zoomStep + 1)
              } else {
                setZoomStep(zoomStep - 1)
              }

              setTimeout(() => {
                const newScale = stage.scaleX()
                const newPos = {
                  x: -(mousePointTo.x - pointerPosition.x / newScale) * newScale,
                  y: -(mousePointTo.y - pointerPosition.y / newScale) * newScale,
                }
                stage.position(newPos)
                stage.batchDraw()
              }, 0)
            }}
          >
            {isVisibleMap && (
              <MapLayer
                projectId={projectId}
                onMapNotFound={() => {
                  setVisibleMap(false)
                  message.warning(intl.formatMessage({id: 'mapNotFound'}))
                }}
                canvasPixelsPerM={canvasPixelsPerCm * 100}
              />
            )}
            <Layer ref={elementsLayerRef}>
              <Group>
                <CanvasEditorShape
                  element={{
                    type: 'plot',
                    ...plotShape,
                    props: {
                      ...plotShape?.props,
                      //     fillPatternImage: image,
                      fillPatternScale: {x: 1 / canvasPixelsPerCm, y: 1 / canvasPixelsPerCm},
                    },
                  }}
                  isEditable={plotShapeEditable}
                  isSelectable={true}
                  rotatable={false}
                  isSelected={selectedElementId === -1}
                  isTwiceSelected={twiceSelectedElementIndex === -1}
                  onSelect={() => {
                    setSelectedElementId(-1)
                    setTwiceSelectedElementIndex(null)
                  }}
                  onChange={onPlotShapeChanged}
                  onTwiceClick={() => {
                    setTwiceSelectedElementIndex(-1)
                  }}
                  setCursor={setCursor}
                  resetCursor={resetCursor}
                  transformerLayerProps={transformerLayerProps}
                />
                {elements?.map((element, index) => (
                  <CanvasEditorShape
                    key={index}
                    element={{
                      ...element,
                      props: {
                        ...element?.props,
                        // fillPatternScale: {x: element., y: 1 / canvasPixelsPerCm},
                      },
                    }}
                    isEditable={editableTypes?.includes(element.type) || false}
                    isSelectable={true}
                    isSelected={element.elementId === selectedElementId}
                    isTwiceSelected={index === twiceSelectedElementIndex}
                    rotatable
                    onSelect={() => {
                      setSelectedElementId(element.elementId)
                      setTwiceSelectedElementIndex(null)
                      setSelectedPlant(null)
                    }}
                    onChange={(modified) => {
                      onElementChanged({...modified, elementId: element.elementId})
                    }}
                    onTwiceClick={() => {
                      setTwiceSelectedElementIndex(index)
                    }}
                    setCursor={setCursor}
                    resetCursor={resetCursor}
                    transformerLayerProps={transformerLayerProps}
                  ></CanvasEditorShape>
                ))}
                {plants?.map((plant, index) =>
                  plant.coordinates
                    //.filter((coord) => Object.keys(coord).length !== 0)
                    .map(
                      (plantSpecimenCoordinates, specimenIndex) =>
                        Object.keys(plantSpecimenCoordinates).length !== 0 ? (
                          <CanvasEditorPlant
                            key={`${index}-${specimenIndex}`}
                            plant={plant}
                            specimenIndex={
                              plant.coordinates.length === 1 ? undefined : specimenIndex
                            }
                            isEditable={
                              (editableTypes?.includes('plant') &&
                                !lockedPlantIds.includes(plant.id)) ||
                              false
                            }
                            isSelectable={true}
                            isSelected={
                              plant.id === selectedPlant?.id &&
                              (typeof selectedPlant.specimenIndex === 'undefined' ||
                                selectedPlant.specimenIndex === specimenIndex)
                            }
                            rotatable
                            onSelect={(evt) => {
                              setSelectedPlant({id: plant.id, specimenIndex})
                            }}
                            onChange={(modified) => {
                              onElementChanged({
                                ...modified,
                                elementId: plant.id,
                                specimenIndex: specimenIndex,
                              })
                            }}
                            onTwiceClick={() => {
                              openPlantById(plant.id)
                              //setSelectedPlant(null)
                              //setTwiceSelectedElementIndex(index)
                            }}
                            setCursor={setCursor}
                            resetCursor={resetCursor}
                          ></CanvasEditorPlant>
                        ) : null
                      // <div>
                      //   <Button onClick={(e) => createNewPlantSpecimen(plant, specimenIndex)}>
                      //     <CopyOutlined />
                      //   </Button>
                      //   <Button onClick={(e) => deletePlantSpecimen(plant, specimenIndex)}>
                      //     <DeleteOutlined />
                      //   </Button>
                      // </div>
                    )
                )}
              </Group>
            </Layer>
            {toolLayer}
            {isGridVisible && <GridLayer gridStepInPx={gridSpacing * 100} />}
            {isRulerActive && (
              <RulerLayer
                canvasPixelsPerM={canvasPixelsPerCm}
                setCursor={setCursor}
                resetCursor={resetCursor}
              />
            )}
            {/* {activeDraggableElement && (
          <DraggableElementLayer
            canvasPixelsPerM={canvasPixelsPerCm}
            setCursor={setCursor}
            resetCursor={resetCursor}
            activeDraggableElement={activeDraggableElement}
            onDragEnd={onDragEnd}
          />
        )} */}
          </Stage>
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          right: '20px',
          top: '20px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        <Compass rotationAngle={0} />
      </div>
      <div className="zoom-scale">
        <ZoomScaleLine pixelsPerCm={canvasPixelsPerCm} />
        <ZoomSliderMemoized
          step={zoomStep}
          setStep={setZoomStep}
          steps={zoomSliderSteps}
          min={zoomSliderMin}
          max={zoomSliderMax}
        />
      </div>
    </Card>
  )
}
